import { BedDouble, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  highlightMatch,
  SearchCombobox,
  type ComboboxGroup,
  type ComboboxItem,
} from '@/components/ui/searchable-select';
import { getSupplier, searchStaySites, type SiteHit, type Supplier } from './api';
import { CreateSupplierDialog, type CreateResult } from './CreateSupplierDialog';
import { CategoryIcon } from './icons';
import { useServerSearch } from './use-supplier-search';

const CREATE = 'create';

/** The property a supplier stands for: its registered office if active, else its first site. */
function propertySite(supplier: Supplier): string | null {
  const active = supplier.sites.filter((site) => site.active);
  return (active.find((site) => site.registeredOffice) ?? active[0])?.id ?? null;
}

/**
 * «Dove hai dormito? · facoltativo» (boards 1.9, 1.12; report §3.1 bis): asked when the supplier is
 * an intermediary. The supplier stays whoever issued the document; the property is a site of
 * another supplier, from the catalog (`searchSupplierSites`) or written new. It goes on the expense
 * as `stayPropertySiteId`.
 */
export function StayPropertyField({
  id,
  intermediaryName,
  value,
  label,
  onChange,
}: {
  readonly id: string;
  readonly intermediaryName: string;
  readonly value: string;
  /** The chosen property's words, when known; an expense read back has only the id. */
  readonly label: string;
  readonly onChange: (siteId: string, label: string) => void;
}) {
  const { t } = useTranslation('suppliers');
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState<string | null>(null);
  const search = useServerSearch(query, searchStaySites);
  const hintId = `${id}-hint`;

  const row = (hit: SiteHit): ComboboxItem => {
    const sign = hit.sign ?? hit.supplierName;
    const owner =
      hit.sign && hit.sign !== hit.supplierName
        ? t('picker.stay.siteOf', { name: hit.supplierName })
        : null;
    return {
      id: hit.siteId,
      leading: <CategoryIcon type={hit.detail ?? hit.macro} />,
      primary: highlightMatch(sign, query),
      secondary: [owner, hit.city].filter(Boolean).join(' · ') || undefined,
      trailing: <span className="tabular-nums">{t('picker.uses', { count: hit.uses })}</span>,
    };
  };
  const groups: ComboboxGroup[] = [
    { id: 'catalog', label: t('picker.stay.group'), items: search.items.map(row) },
  ];
  const name = query.trim();

  const onCreated = async (result: CreateResult): Promise<void> => {
    setCreating(null);
    setQuery('');
    if (result.kind === 'supplier') {
      const siteId = result.siteId ?? propertySite(result.supplier);
      if (siteId) onChange(siteId, result.supplier.name);
      return;
    }
    // An existing supplier was chosen in the window: its property is its site.
    const read = await getSupplier(result.pick.id);
    const siteId = read.ok ? propertySite(read.value) : null;
    if (siteId) onChange(siteId, result.pick.name);
  };

  return (
    <div className="flex flex-col gap-1.5" data-field="stayProperty">
      <label htmlFor={id} className="text-sm font-medium">
        {t('picker.stay.label')}{' '}
        <span className="text-muted-foreground font-normal">{t('picker.stay.optional')}</span>
      </label>
      {/* Above the field, so the open list never covers it (board 1.9). */}
      <small id={hintId} className="text-muted-foreground text-xs">
        {t('picker.stay.hint', { name: intermediaryName })}
      </small>
      {value !== '' ? (
        <div
          id={id}
          role="group"
          aria-label={label || t('picker.stay.chosenUnknown')}
          className="border-border bg-card flex min-h-11 items-center gap-3 rounded-md border py-1 pr-1 pl-3"
        >
          <BedDouble aria-hidden="true" className="text-muted-foreground size-4" />
          <b className="min-w-0 flex-1 truncate text-sm">
            {label || t('picker.stay.chosenUnknown')}
          </b>
          <Button
            type="button"
            variant="ghost"
            className="size-11 shrink-0 p-0"
            aria-label={t('picker.stay.change')}
            onClick={() => onChange('', '')}
          >
            <X aria-hidden="true" />
          </Button>
        </div>
      ) : (
        <SearchCombobox
          id={id}
          query={query}
          onQueryChange={setQuery}
          groups={groups}
          actions={
            name.length >= 2
              ? [
                  {
                    id: CREATE,
                    leading: <Plus className="size-4" />,
                    primary: t('picker.stay.create', { name }),
                    secondary: t('picker.stay.createHint'),
                  },
                ]
              : []
          }
          status={search.status}
          onSelect={(siteId) => {
            const hit = search.items.find((item) => item.siteId === siteId);
            if (!hit) return;
            setQuery('');
            onChange(hit.siteId, hit.sign ?? hit.supplierName);
          }}
          onAction={() => setCreating(name)}
          listLabel={t('picker.stay.listLabel')}
          searchingLabel={t('picker.searching')}
          emptyLabel={t('picker.stay.empty')}
          placeholder={t('picker.stay.placeholder')}
          leading={<BedDouble className="size-4" />}
          errorSlot={
            <p role="alert" className="text-destructive px-3 py-2 text-sm">
              {t('picker.searchDown.title')} {t('picker.searchDown.text')}
            </p>
          }
          aria-describedby={hintId}
        />
      )}
      {creating !== null && (
        <CreateSupplierDialog
          stay
          initial={{ name: creating }}
          onDone={(result) => void onCreated(result)}
          onClose={() => setCreating(null)}
        />
      )}
    </div>
  );
}
